import { Cartouche } from '@brig/theme/components';
import { CompassRose } from '@brig/theme/ornaments';
import { button, paper } from '@brig/theme/styles';
import { DEFAULT_RULESET } from '@brig/rules-engine';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useMe, useUpdateMe } from '../api/hooks.ts';
import { useAuth } from '../auth/useAuth.ts';
import { LanguageSwitcher } from '../components/LanguageSwitcher.tsx';
import { ShipSprite } from '../ships/ShipSprite.tsx';
import { ShipsTable, type ShipsTableKind } from '../ships/ShipsTable.tsx';
import { useDocumentTitle } from '../useDocumentTitle.ts';
import * as styles from './HowToPlayPage.css.ts';
import * as own from './ShipsPage.css.ts';

const KINDS: readonly ShipsTableKind[] = ['movement', 'combat'];

/**
 * The ship classes in two tables, movement and combat, open to guests like "How to play": the first thing a new
 * captain wants to compare. Outside the app shell, so it carries its own way home.
 */
export function ShipsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('shipsPage.pageTitle'));
  const { status } = useAuth();
  const { data: me } = useMe(status === 'authenticated');
  const updateMe = useUpdateMe();
  return (
    <main className={styles.sea}>
      <header className={`${styles.topBar} ${own.wide}`}>
        <Link to="/" className={styles.brand}>
          <CompassRose size={40} northLabel="" ink="#2F2418" paper="#F1E6C8" accent="#A8863F" />
          <span className={styles.brandName}>{t('app.shortName')}</span>
        </Link>
        <LanguageSwitcher
          onChange={(locale) => {
            if (me && me.locale !== locale) updateMe.mutate({ locale });
          }}
        />
      </header>
      <section className={`${paper} ${styles.sheet} ${own.wide}`}>
        <Cartouche subtitle={t('shipsPage.lead')}>{t('shipsPage.title')}</Cartouche>
        <p className={own.body}>{t('shipsPage.intro')}</p>
        {/* The vector tokens, for the fallback art: once for both tables. */}
        <ShipSprite />
        {KINDS.map((kind) => (
          <section key={kind} className={own.part} aria-labelledby={`ships-${kind}`}>
            <h2 id={`ships-${kind}`} className={own.heading}>
              {t(`shipsPage.${kind}.title`)}
            </h2>
            <p className={own.body}>{t(`shipsPage.${kind}.body`)}</p>
            <ShipsTable ruleset={DEFAULT_RULESET} kind={kind} />
          </section>
        ))}
        <div className={own.links}>
          <Link to="/how-to-play" className={button({ tone: 'label' })}>
            {t('learn.open')}
          </Link>
          <Link to="/" className={button({ tone: 'seal' })}>
            {t('learn.back')}
          </Link>
        </div>
      </section>
    </main>
  );
}
